/** Cấu hình biểu mẫu chứng nhận (layout_config JSON trên server) — mức 2 IEC/IMA */

export const CERT_CANVAS = { width: 842, height: 595 };

export const CERT_FONT_OPTIONS = [
  {
    id: "times",
    name: "Times New Roman",
    cssFamily: "'Times New Roman', Times, serif",
  },
  {
    id: "georgia",
    name: "Georgia",
    cssFamily: "Georgia, 'Times New Roman', serif",
  },
  {
    id: "arial",
    name: "Arial",
    cssFamily: "Arial, Helvetica, sans-serif",
  },
  {
    id: "playfair",
    name: "Playfair Display",
    cssFamily: "'Playfair Display', Georgia, serif",
  },
  {
    id: "charm",
    name: "Charm",
    cssFamily: "'Charm', cursive",
  },
];

export const CERT_FONT_IMPORT_CSS =
  "@import url('https://fonts.googleapis.com/css2?family=Charm:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap');";

export const CERT_DEFAULTS = {
  issue_place: "Nghệ An",
  organization_unit: "IMA Việt Nam",
  cert_id_suffix: "/CN- IMA Việt Nam",
  validity_months: 6,
};

const STUDENT_NAME_CERT_PREFIX = "Em: ";

export function formatRecipientNameForCert(name, recipientType) {
  const raw = String(name || "").trim();
  if (!raw || raw === "—") return "—";
  if (recipientType === "teacher") return raw;
  if (raw.startsWith(STUDENT_NAME_CERT_PREFIX)) return raw;
  return `${STUDENT_NAME_CERT_PREFIX}${raw}`;
}

export const CERT_FIELD_LABELS = {
  course_name: "Tên khóa học",
  student_name: "Tên học viên / Giáo viên",
  birth_year: "Năm sinh",
  organization_unit: "Đơn vị",
  grade: "Kết quả",
  issue_place: "Nơi cấp",
  issue_date_text: "Ngày cấp (dạng văn bản)",
  issue_date: "Ngày cấp (dd/mm/yyyy)",
  expiry_date: "Hết hiệu lực",
  cert_serial: "Số hiệu (phần số)",
  cert_id_display: "Số hiệu đầy đủ",
  cert_id: "Mã chứng nhận (gốc)",
  register_no_display: "Số vào sổ (hiển thị)",
  register_no: "Số vào sổ (gốc)",
};

export const DEFAULT_FIELD_KEYS = Object.keys(CERT_FIELD_LABELS);

export function getCertificateFontFamily(fontKey = "times") {
  const raw = String(fontKey || "").trim();
  if (!raw) {
    return CERT_FONT_OPTIONS[0].cssFamily;
  }
  const match = CERT_FONT_OPTIONS.find((item) => item.id === raw);
  return match?.cssFamily || raw;
}

function parseIsoDate(value) {
  if (value == null || value === "") return null;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10), d: parseInt(m[3], 10) };
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate() };
}

export function formatIssueDateForCert(value) {
  const p = parseIsoDate(value);
  if (!p) return value == null || value === "" ? "—" : String(value);
  const dd = String(p.d).padStart(2, "0");
  const mm = String(p.mo).padStart(2, "0");
  return `${dd}/${mm}/${p.y}`;
}

export function formatIssueDateVietnamese(value) {
  const p = parseIsoDate(value);
  if (!p) return "—";
  return `ngày ${p.d} tháng ${p.mo} năm ${p.y}`;
}

export function formatExpiryDateForCert(value) {
  return formatIssueDateForCert(value);
}

export function extractBirthYearFromDob(dob) {
  const p = parseIsoDate(dob);
  return p ? p.y : null;
}

export function addMonthsToIsoDate(isoDate, months) {
  const p = parseIsoDate(isoDate);
  if (!p) return null;
  const dt = new Date(p.y, p.mo - 1 + Number(months), p.d);
  const y = dt.getFullYear();
  const mo = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

export function computeExpiryDate(issueDate, validityMonths = CERT_DEFAULTS.validity_months) {
  return addMonthsToIsoDate(issueDate, validityMonths);
}

export function formatCertSerial(certCode) {
  if (certCode == null || certCode === "") return "—";
  const s = String(certCode);
  const m = s.match(/(\d+)\s*$/);
  if (m) return String(parseInt(m[1], 10)).padStart(3, "0");
  return s;
}

export function formatCertIdDisplay(certCode, suffix = CERT_DEFAULTS.cert_id_suffix) {
  const serial = formatCertSerial(certCode);
  if (serial === "—") return "—";
  const sfx = suffix != null && String(suffix).trim() !== "" ? String(suffix).trim() : "";
  return sfx ? `${serial}${sfx.startsWith("/") ? sfx : ` ${sfx}`}` : serial;
}

export function formatRegisterNoDisplay(registerNo) {
  if (registerNo == null || registerNo === "") return "—";
  const m = String(registerNo).match(/^SVS-(\d{4})-(\d+)$/i);
  if (m) {
    const seq = parseInt(m[2], 10);
    return `${String(seq).padStart(3, "0")}/${m[1]}`;
  }
  return String(registerNo);
}

export function getLayoutDefaults(layoutConfig) {
  const p = normalizeLayoutConfig(layoutConfig) || {};
  return { ...CERT_DEFAULTS, ...(p.defaults || {}) };
}

/** Gộp dữ liệu hiển thị từ bản ghi API + mặc định biểu mẫu */
export function buildCertificateDisplayData(row, layoutConfig = null) {
  if (!row) return {};
  const defaults = getLayoutDefaults(layoutConfig);
  const extra =
    row.extra_data && typeof row.extra_data === "object"
      ? row.extra_data
      : {};

  const issueDate = row.issue_date ?? extra.issue_date ?? null;
  const validityMonths = row.validity_months ?? extra.validity_months ?? defaults.validity_months;
  const expiryDate =
    row.expiry_date ?? extra.expiry_date ?? computeExpiryDate(issueDate, validityMonths);

  const certCode = row.cert_id ?? row.cert_code ?? null;
  const registerNo = row.register_no ?? null;
  const suffix = row.cert_id_suffix ?? extra.cert_id_suffix ?? defaults.cert_id_suffix;

  const studentName =
    row.student_name ?? row.recipient_name ?? row.teacher_name ?? null;

  const birthYear =
    row.birth_year ??
    extra.birth_year ??
    extractBirthYearFromDob(row.dob ?? row.birthdate ?? row.birthday);

  const organizationUnit =
    row.organization_unit ?? extra.organization_unit ?? defaults.organization_unit;

  const issuePlace = row.issue_place ?? extra.issue_place ?? defaults.issue_place;

  return {
    recipient_type: row.recipient_type ?? null,
    student_name: studentName || "—",
    course_name: row.course_name || "—",
    subject: row.subject ?? row.subject_name ?? "—",
    grade: row.grade || "—",
    birth_year: birthYear != null ? String(birthYear) : "—",
    organization_unit: organizationUnit || "—",
    issue_place: issuePlace || "—",
    issue_date: issueDate,
    issue_date_text: formatIssueDateVietnamese(issueDate),
    expiry_date: expiryDate,
    cert_id: certCode || "—",
    cert_serial: formatCertSerial(certCode),
    cert_id_display: formatCertIdDisplay(certCode, suffix),
    register_no: registerNo || "—",
    register_no_display: formatRegisterNoDisplay(registerNo),
    cert_id_suffix: suffix,
  };
}

export function getCertFieldDisplayValue(key, data, layoutConfig = null) {
  if (!data) return "—";
  const defaults = getLayoutDefaults(layoutConfig);
  switch (key) {
    case "issue_date":
      return formatIssueDateForCert(data.issue_date);
    case "issue_date_text":
      return data.issue_date_text || formatIssueDateVietnamese(data.issue_date);
    case "expiry_date":
      return formatExpiryDateForCert(data.expiry_date);
    case "student_name":
      return formatRecipientNameForCert(data.student_name, data.recipient_type);
    case "course_name":
      return data.course_name || "—";
    case "grade":
      return data.grade || "—";
    case "birth_year":
      return data.birth_year != null && data.birth_year !== "" ? String(data.birth_year) : "—";
    case "organization_unit":
      return data.organization_unit || "—";
    case "issue_place":
      return data.issue_place || "—";
    case "cert_serial":
      return data.cert_serial || formatCertSerial(data.cert_id);
    case "cert_id_display":
      return (
        data.cert_id_display ||
        formatCertIdDisplay(data.cert_id, data.cert_id_suffix ?? defaults.cert_id_suffix)
      );
    case "cert_id":
      return data.cert_id || "—";
    case "register_no_display":
      return data.register_no_display || formatRegisterNoDisplay(data.register_no);
    case "register_no":
      return data.register_no || "—";
    default:
      return data[key] != null && data[key] !== "" ? String(data[key]) : "—";
  }
}

export function normalizeLayoutConfig(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return typeof raw === "object" ? raw : null;
}

function withDefaultFieldStyle(field) {
  return {
    fontFamily: "times",
    fontStyle: "normal",
    textTransform: "none",
    ...field,
    fontFamily: field?.fontFamily || "times",
    fontStyle: field?.fontStyle || "normal",
    textTransform: field?.textTransform || "none",
  };
}

/** Danh sách định nghĩa đầy đủ 14 key (vị trí/style mặc định) */
export const ALL_FIELD_DEFINITIONS = [
  { key: "student_name", x: 0.22, y: 0.44, fontSize: 18, width: 280, color: "#111827", align: "left", fontWeight: "bold" },
  { key: "birth_year", x: 0.22, y: 0.5, fontSize: 16, width: 120, color: "#111827", align: "left", fontWeight: "normal" },
  { key: "organization_unit", x: 0.22, y: 0.56, fontSize: 16, width: 320, color: "#111827", align: "left", fontWeight: "normal" },
  {
    key: "course_name",
    x: 0.5,
    y: 0.62,
    fontSize: 18,
    width: 520,
    color: "#b91c1c",
    align: "center",
    fontWeight: "bold",
  },
  { key: "issue_place", x: 0.72, y: 0.72, fontSize: 16, width: 200, color: "#111827", align: "right", fontWeight: "normal" },
  {
    key: "issue_date_text",
    x: 0.72,
    y: 0.78,
    fontSize: 16,
    width: 280,
    color: "#111827",
    align: "right",
    fontWeight: "normal",
  },
  { key: "cert_id_display", x: 0.12, y: 0.9, fontSize: 13, width: 220, color: "#111827", align: "left", fontWeight: "normal" },
  {
    key: "register_no_display",
    x: 0.12,
    y: 0.94,
    fontSize: 13,
    width: 160,
    color: "#111827",
    align: "left",
    fontWeight: "normal",
  },
  {
    key: "expiry_date",
    x: 0.12,
    y: 0.98,
    fontSize: 13,
    width: 140,
    color: "#111827",
    align: "left",
    fontWeight: "normal",
  },
  { key: "grade", x: 0.5, y: 0.68, fontSize: 18, width: 200, color: "#374151", align: "center", fontWeight: "normal" },
  { key: "issue_date", x: 0.5, y: 0.74, fontSize: 14, width: 180, color: "#4b5563", align: "center", fontWeight: "normal" },
  { key: "cert_serial", x: 0.12, y: 0.86, fontSize: 13, width: 80, color: "#111827", align: "left", fontWeight: "normal" },
  { key: "cert_id", x: 0.5, y: 0.88, fontSize: 12, width: 190, color: "#9ca3af", align: "center", fontWeight: "normal" },
  { key: "register_no", x: 0.5, y: 0.92, fontSize: 12, width: 220, color: "#9ca3af", align: "center", fontWeight: "normal" },
].map(withDefaultFieldStyle);

const CATALOG_BY_KEY = new Map(ALL_FIELD_DEFINITIONS.map((f) => [f.key, withDefaultFieldStyle(f)]));

/** Key bật mặc định khi tạo biểu mẫu mới (mẫu IEC) */
export const IEC_PRESET_KEYS = [
  "student_name",
  "birth_year",
  "organization_unit",
  "course_name",
  "issue_place",
  "issue_date_text",
  "cert_id_display",
  "register_no_display",
  "expiry_date",
];

export function getFieldDefinition(key) {
  return CATALOG_BY_KEY.get(key)
    ? { ...CATALOG_BY_KEY.get(key) }
    : withDefaultFieldStyle({ key, x: 0.5, y: 0.5, fontSize: 16, align: "center" });
}

function buildFieldsFromKeys(keys) {
  return keys.map((key) => getFieldDefinition(key));
}

/** Chỉ giữ field đã lưu trong biểu mẫu; bổ sung style từ catalog */
function resolveSavedFields(parsedFields) {
  const saved = Array.isArray(parsedFields) ? parsedFields.filter((f) => f && f.key) : [];
  if (saved.length === 0) {
    return buildFieldsFromKeys(IEC_PRESET_KEYS);
  }
  return saved.map((f) => withDefaultFieldStyle({ ...getFieldDefinition(f.key), ...f }));
}

export function getEnabledFieldKeys(layout) {
  const fields = layout?.fields;
  return Array.isArray(fields) ? fields.map((f) => f.key) : [];
}

export function isFieldKeyEnabled(layout, key) {
  return getEnabledFieldKeys(layout).includes(key);
}

export function createDefaultLayoutConfig() {
  return {
    version: 2,
    width: CERT_CANVAS.width,
    height: CERT_CANVAS.height,
    backgroundUrl: null,
    defaults: { ...CERT_DEFAULTS },
    fields: buildFieldsFromKeys(IEC_PRESET_KEYS),
  };
}

export function mergeLayoutWithDefaults(raw) {
  const parsed = normalizeLayoutConfig(raw);
  const base = createDefaultLayoutConfig();
  if (!parsed) return base;
  return {
    ...base,
    ...parsed,
    width: parsed.width ?? CERT_CANVAS.width,
    height: parsed.height ?? CERT_CANVAS.height,
    defaults: { ...base.defaults, ...(parsed.defaults || {}) },
    fields: resolveSavedFields(parsed.fields),
  };
}

export function isDynamicCertificateLayout(layout) {
  const p = normalizeLayoutConfig(layout);
  if (!p) return false;
  if (p.backgroundUrl && String(p.backgroundUrl).trim()) return true;
  return Array.isArray(p.fields) && p.fields.length > 0;
}
